import {
  createRescueConversationRequestSchema,
  getRescueConversationResponseSchema,
  rescueConversationErrorResponseSchema,
  rescueConversationResponseSchema,
  respondRescueConversationRequestSchema,
  type RespondRescueConversationRequest,
} from "@io-spoke/contracts/rescueConversation";
import { getApiClient } from "./apiClient";

export class RescueConversationConflict extends Error {}

const conversationPath = (caseId: string) =>
  `/rescues/${encodeURIComponent(caseId)}/conversation`;
const mutationConfig = {
  validateStatus: (status: number) =>
    status === 409 || (status >= 200 && status < 300),
};

function parseMutation(response: { status: number; data: unknown }) {
  if (response.status === 409) {
    const parsed = rescueConversationErrorResponseSchema.safeParse(
      response.data,
    );
    throw new RescueConversationConflict(
      parsed.success
        ? parsed.data.error
        : "This investigation changed. Reload its latest status.",
    );
  }
  const parsed = rescueConversationResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error(
      "The investigation returned an invalid response. Reload and try again.",
    );
  return parsed.data.conversation;
}

export async function getRescueConversation(caseId: string) {
  const response = await getApiClient().get(conversationPath(caseId));
  const parsed = getRescueConversationResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error(
      "The saved investigation returned an invalid response. Reload and try again.",
    );
  return parsed.data.conversation;
}

export async function createRescueConversation(caseId: string) {
  const payload = createRescueConversationRequestSchema.parse({});
  return parseMutation(
    await getApiClient().post(
      conversationPath(caseId),
      payload,
      mutationConfig,
    ),
  );
}

export async function respondRescueConversation(
  caseId: string,
  conversationId: string,
  input: RespondRescueConversationRequest,
) {
  const payload = respondRescueConversationRequestSchema.parse(input);
  return parseMutation(
    await getApiClient().post(
      `${conversationPath(caseId)}/${encodeURIComponent(conversationId)}/respond`,
      payload,
      mutationConfig,
    ),
  );
}
